import { useEffect, useMemo, useState } from "react";
import { Zap, Plus, Pencil, Trash2, Loader2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const fillQuickReply = (text, contact) => {
  const fields = { nome: contact?.name || "", name: contact?.name || "", telefono: contact?.phone || "", phone: contact?.phone || "" };
  return (text || "").replace(/\{\{\s*([^}|]+?)\s*(?:\|([^}]*))?\}\}/g, (_, k, d) => {
    const v = fields[k.trim()];
    return v || (d ?? "").trim() || (k.trim() === "nome" ? "Cliente" : "");
  });
};

const EMPTY = { title: "", text: "", shortcut: "" };

function QuickReplyForm({ item, onDone, onCancel }) {
  const [form, setForm] = useState(item || EMPTY);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim() || !form.text.trim()) { toast.error("Titolo e testo sono obbligatori"); return; }
    setSaving(true);
    try {
      const res = item?.id ? await api.put(`/chat/quick-replies/${item.id}`, form) : await api.post("/chat/quick-replies", form);
      toast.success(item?.id ? "Risposta rapida aggiornata" : "Risposta rapida creata");
      onDone(res.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 bg-[#0A0E17] border border-[#1F2937] rounded-lg p-3" data-testid="quick-reply-form">
      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="quick-reply-title" placeholder="Titolo, es. Orari di apertura" className="h-8 bg-[#111827] border-[#1F2937] text-slate-100 text-sm" />
      <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} data-testid="quick-reply-text"
                placeholder="Testo della risposta. Puoi usare {{nome}} per il nome del cliente." className="bg-[#111827] border-[#1F2937] text-slate-100 text-sm" />
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 shrink-0">Scorciatoia /</span>
        <Input value={form.shortcut || ""} onChange={(e) => setForm({ ...form, shortcut: e.target.value.replace(/^\//, "") })} data-testid="quick-reply-shortcut" placeholder="orari" className="h-8 bg-[#111827] border-[#1F2937] text-slate-100 text-sm font-mono" />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="text-slate-400 h-8">Annulla</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving} data-testid="quick-reply-save" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}
        </Button>
      </div>
    </div>
  );
}

export function QuickRepliesManager({ open, onClose, items, reload }) {
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (!open) setEditing(null); }, [open]);
  const remove = async (it) => {
    if (!window.confirm(`Eliminare «${it.title}»?`)) return;
    try { await api.delete(`/chat/quick-replies/${it.id}`); toast.success("Eliminata"); reload(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg" data-testid="quick-replies-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#F59E0B]" /> Risposte rapide</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Risposte salvate per le domande più comuni, condivise con tutti gli operatori. In chat digita <span className="font-mono text-slate-200">/</span> seguito dalla scorciatoia oppure usa il fulmine.</p>
        {editing ? (
          <QuickReplyForm item={editing.id ? editing : null} onDone={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />
        ) : (
          <Button type="button" size="sm" onClick={() => setEditing(EMPTY)} data-testid="quick-reply-new" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold w-full h-8">
            <Plus className="h-3.5 w-3.5 mr-1" /> Nuova risposta rapida
          </Button>
        )}
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {!items.length && !editing && <p className="text-sm text-slate-500 text-center py-6">Nessuna risposta rapida ancora.</p>}
          {items.map((it) => (
            <div key={it.id} className="flex items-start gap-2 bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2" data-testid={`quick-reply-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-100 font-medium truncate">{it.title}{it.shortcut && <span className="ml-2 font-mono text-[10px] text-[#F59E0B]">/{it.shortcut}</span>}</p>
                <p className="text-xs text-slate-400 line-clamp-2 whitespace-pre-wrap">{it.text}</p>
                {it.uses > 0 && <p className="text-[10px] text-slate-600 mt-0.5">usata {it.uses} volte</p>}
              </div>
              <button type="button" onClick={() => setEditing(it)} data-testid={`quick-reply-edit-${it.id}`} className="text-slate-400 hover:text-slate-200 shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(it)} data-testid={`quick-reply-delete-${it.id}`} className="text-slate-400 hover:text-[#EF4444] shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuickRepliesPicker({ items, contact, onPick, onManage, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^\//, "");
    return items.filter((it) => !s || it.title.toLowerCase().includes(s) || (it.shortcut || "").includes(s) || it.text.toLowerCase().includes(s));
  }, [items, q]);
  const pick = (it) => {
    onPick(fillQuickReply(it.text, contact), it);
    setOpen(false); setQ("");
    api.post(`/chat/quick-replies/${it.id}/use`).catch(() => {});
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} data-testid="chat-quick-replies-button" title="Risposte rapide"
                className="bg-[#1E293B] border-[#1F2937] text-[#F59E0B] hover:text-[#FBBF24] shrink-0 px-2.5">
          <Zap className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2 bg-[#111827] border-[#1F2937] text-slate-100" data-testid="chat-quick-replies-popover">
        <div className="flex items-center gap-2 mb-2">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="chat-quick-replies-search" placeholder="Cerca risposta rapida…"
                 onKeyDown={(e) => { if (e.key === "Enter" && list[0]) { e.preventDefault(); pick(list[0]); } }}
                 className="h-8 bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
          <button type="button" onClick={() => { setOpen(false); onManage(); }} data-testid="chat-quick-replies-manage" title="Gestisci risposte rapide" className="text-slate-400 hover:text-slate-200 shrink-0"><Settings2 className="h-4 w-4" /></button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {!items.length && (
            <button type="button" onClick={() => { setOpen(false); onManage(); }} data-testid="chat-quick-replies-empty" className="w-full text-left px-2 py-2 rounded text-xs text-[#10B981] hover:bg-[#1F2937] flex items-center gap-1.5">
              <Plus className="h-3 w-3" /> Crea la prima risposta rapida
            </button>
          )}
          {items.length > 0 && !list.length && <p className="text-[11px] text-slate-500 px-2 py-2">Nessuna risposta corrisponde.</p>}
          {list.map((it) => (
            <button key={it.id} type="button" onClick={() => pick(it)} data-testid={`chat-quick-reply-${it.id}`}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-[#1F2937] transition-colors">
              <p className="text-xs text-slate-100 font-medium truncate">{it.title}{it.shortcut && <span className="ml-2 font-mono text-[10px] text-[#F59E0B]">/{it.shortcut}</span>}</p>
              <p className="text-[11px] text-slate-400 truncate">{fillQuickReply(it.text, contact)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function SlashSuggestions({ text, items, contact, onPick }) {
  if (!text.startsWith("/") || text.includes(" ")) return null;
  const s = text.slice(1).toLowerCase();
  const list = items.filter((it) => (it.shortcut || "").startsWith(s) || it.title.toLowerCase().startsWith(s)).slice(0, 6);
  if (!list.length) return null;
  return (
    <div className="rounded-lg border border-[#1F2937] bg-[#111827] p-1 space-y-0.5" data-testid="chat-slash-suggestions">
      {list.map((it) => (
        <button key={it.id} type="button" onClick={() => { onPick(fillQuickReply(it.text, contact), it); api.post(`/chat/quick-replies/${it.id}/use`).catch(() => {}); }}
                data-testid={`chat-slash-${it.id}`} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#1F2937] flex items-center gap-2">
          <Zap className="h-3 w-3 text-[#F59E0B] shrink-0" />
          <span className="text-xs text-slate-100 font-medium truncate">{it.title}</span>
          {it.shortcut && <span className="font-mono text-[10px] text-[#F59E0B]">/{it.shortcut}</span>}
          <span className="text-[11px] text-slate-500 truncate flex-1">{it.text}</span>
        </button>
      ))}
    </div>
  );
}
